import { Button, Col, DatePicker, Form, Input, Modal, Radio, Row, Select, message } from "antd";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Country, State, City } from "country-state-city";
import dayjs from "dayjs";
import { updateMe } from "../../redux/slice/authSlice";

// The popup keeps showing until every one of these is saved on the profile.
const REQUIRED_FIELDS = ["gender", "dateOfBirth", "countryCode", "state", "city", "postalCode"];

const readStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user")) || null;
  } catch (error) {
    return null;
  }
};

const filterOption = (input, option) =>
  (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

const ExtraInfo = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { personalDetail } = useSelector(({ auth }) => auth);
  const [storedUser, setStoredUser] = useState(readStoredUser);
  const [saving, setSaving] = useState(false);

  // The freshly fetched profile wins over the copy saved at login.
  const profile = useMemo(
    () => ({ ...storedUser, ...(personalDetail?.email ? personalDetail : {}) }),
    [storedUser, personalDetail]
  );
  const isComplete = REQUIRED_FIELDS.every((field) => !!profile[field]);

  const countries = useMemo(() => Country.getAllCountries(), []);
  const [countryCode, setCountryCode] = useState(profile.countryCode || null);
  const states = useMemo(
    () => (countryCode ? State.getStatesOfCountry(countryCode) : []),
    [countryCode]
  );
  const initialStateCode =
    states.find((s) => s.name === profile.state)?.isoCode || profile.state || undefined;
  const [stateCode, setStateCode] = useState(initialStateCode || null);
  const cities = useMemo(
    () => (countryCode && stateCode ? City.getCitiesOfState(countryCode, stateCode) : []),
    [countryCode, stateCode]
  );

  if (!storedUser || isComplete) {
    return null;
  }

  const initialValues = {
    gender: profile.gender ? String(profile.gender).toLowerCase() : undefined,
    dob: profile.dateOfBirth && dayjs(profile.dateOfBirth).isValid() ? dayjs(profile.dateOfBirth) : undefined,
    country: profile.countryCode || undefined,
    state: initialStateCode,
    city: profile.city || undefined,
    postalCode: profile.postalCode || undefined,
  };

  const handleCountryChange = (value) => {
    setCountryCode(value);
    setStateCode(null);
    form.setFieldsValue({ state: undefined, city: undefined });
  };

  const handleStateChange = (value) => {
    setStateCode(value);
    form.setFieldsValue({ city: undefined });
  };

  const onFinish = async (values) => {
    const countryObj = countries.find((c) => c.isoCode === values.country);
    const stateObj = states.find((s) => s.isoCode === values.state);

    const payload = {
      gender: values.gender,
      dateOfBirth: values.dob.format("YYYY-MM-DD"),
      country: countryObj?.name || values.country,
      countryCode: values.country,
      state: stateObj?.name || values.state.trim(),
      city: values.city.trim(),
      postalCode: values.postalCode.trim(),
    };
    if (!profile.phoneCode && countryObj?.phonecode) {
      payload.phoneCode = countryObj.phonecode;
    }

    setSaving(true);
    try {
      await dispatch(updateMe(payload)).unwrap();
      // Only now is it safe to remember the details locally — the server has them.
      const updatedUser = { ...storedUser, ...payload };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      setStoredUser(updatedUser);
      message.success("Profile saved");
    } catch (error) {
      message.error(
        typeof error === "string" ? error : "Could not save your details. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      centered
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      width={560}
      className="profile-modal"
    >
      <div className="profile-modal-header">
        <h4>Complete your profile</h4>
        <p>
          These details help us match you with surveys you qualify for. You only
          need to fill this in once.
        </p>
      </div>

      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={initialValues}
        onFinish={onFinish}
        scrollToFirstError
        className="profile-modal-form"
      >
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="gender"
              label="Gender"
              rules={[{ required: true, message: "Please select your gender" }]}
            >
              <Radio.Group optionType="button" className="profile-modal-gender">
                <Radio.Button value="male">Male</Radio.Button>
                <Radio.Button value="female">Female</Radio.Button>
                <Radio.Button value="other">Other</Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="dob"
              label="Date of birth"
              rules={[{ required: true, message: "Please select your date of birth" }]}
            >
              <DatePicker
                size="large"
                format="DD/MM/YYYY"
                placeholder="DD/MM/YYYY"
                style={{ width: "100%" }}
                disabledDate={(current) => current && current.isAfter(dayjs(), "day")}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="country"
              label="Country"
              rules={[{ required: true, message: "Please select your country" }]}
            >
              <Select
                showSearch
                size="large"
                placeholder="Select country"
                onChange={handleCountryChange}
                filterOption={filterOption}
                options={countries.map((c) => ({
                  value: c.isoCode,
                  label: `${c.flag} ${c.name}`,
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="state"
              label="State"
              rules={[{ required: true, whitespace: true, message: "Please enter your state" }]}
            >
              {/* Some countries have no state list — fall back to typing it in. */}
              {countryCode && states.length === 0 ? (
                <Input size="large" placeholder="State / region" onChange={(e) => setStateCode(e.target.value)} />
              ) : (
                <Select
                  showSearch
                  size="large"
                  placeholder="Select state"
                  disabled={!countryCode}
                  onChange={handleStateChange}
                  filterOption={filterOption}
                  options={states.map((s) => ({ value: s.isoCode, label: s.name }))}
                />
              )}
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="city"
              label="City"
              rules={[{ required: true, whitespace: true, message: "Please enter your city" }]}
            >
              {stateCode && cities.length === 0 ? (
                <Input size="large" placeholder="City" />
              ) : (
                <Select
                  showSearch
                  size="large"
                  placeholder="Select city"
                  disabled={!stateCode}
                  filterOption={filterOption}
                  options={cities.map((c) => ({ value: c.name, label: c.name }))}
                />
              )}
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="postalCode"
              label="Postal / Zip code"
              rules={[
                { required: true, whitespace: true, message: "Please enter your postal / zip code" },
                { max: 12, message: "That looks too long for a postal code" },
              ]}
            >
              <Input size="large" placeholder="Postal / Zip code" />
            </Form.Item>
          </Col>
        </Row>

        <Button
          type="primary"
          htmlType="submit"
          size="large"
          block
          loading={saving}
          className="profile-modal-submit"
        >
          Save and continue
        </Button>
      </Form>
    </Modal>
  );
};

export default ExtraInfo;
